/**
 * Which paid add-ons this organization has turned on.
 *
 * The backend refuses every add-on callable with failed-precondition when the
 * add-on is off (functions/src/utils/addons.ts), so this is not access
 * control — it decides what is PRESENT. A module that is off has no sidebar
 * item and its pages show a locked state instead of a list that can never
 * load. A disabled control only teaches someone what they are missing.
 *
 * Read straight from the organization document, which every member may read
 * (firestore.rules: `match /orgs/{orgId} { allow read: if sameOrg(orgId) }`),
 * rather than through getOrgAddons, which is a sysadmin call.
 */

import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../lib/firebase';

/** Mirrors ADDON_KEYS in functions/src/admin/addonFunctions.ts. */
export const ADDON_KEYS = [
  'scopeOfService',
  'formsAttestation',
  'competencyEvidence',
  'exceptionManagement',
  'lippincottIntegration',
] as const;

export type AddonKey = (typeof ADDON_KEYS)[number];

export type Addons = Readonly<Record<AddonKey, boolean>>;

export const NO_ADDONS: Addons = Object.freeze({
  scopeOfService: false,
  formsAttestation: false,
  competencyEvidence: false,
  exceptionManagement: false,
  lippincottIntegration: false,
});

/**
 * The `addons` map of an organization document, read defensively.
 *
 * Shape on the wire: `{ [key]: { enabled: boolean, enabledAt, ... } }`. Only
 * `enabled === true` counts — a key that is present but false, or present
 * with no `enabled` at all, is off, which is also what the backend decides.
 */
export function parseAddons(data: unknown): Addons {
  const map = (data as { addons?: unknown } | null | undefined)?.addons;
  const out: Record<AddonKey, boolean> = { ...NO_ADDONS };
  if (typeof map !== 'object' || map === null) return out;
  for (const key of ADDON_KEYS) {
    const entry = (map as Record<string, unknown>)[key];
    out[key] =
      typeof entry === 'object' && entry !== null && (entry as { enabled?: unknown }).enabled === true;
  }
  return out;
}

export function useAddons(orgId: string | null) {
  return useQuery({
    queryKey: ['addons', orgId],
    enabled: Boolean(orgId),
    // An add-on is switched on by a sysadmin, once. Five minutes of staleness
    // is nothing against a sidebar item that flickers on every navigation.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const db = getFirestore(app);
      const snap = await getDoc(doc(db, 'orgs', orgId!));
      return parseAddons(snap.exists() ? snap.data() : null);
    },
  });
}
